import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Dashboard from './components/dashboard/Dashboard';
import OAuthLogin from './components/auth/OAuthLogin';
import ExistingCardNotice from './components/auth/ExistingCardNotice';
import ShopDetail from './components/shops/ShopDetail';
import ShopOwnerDashboard from './components/shops/ShopOwnerDashboard';
import ShopAnalyticsDashboard from './components/shops/analytics/ShopAnalyticsDashboard';
import ShopRegistration from './components/shops/ShopRegistration';
import SettingsHub from './components/shops/settings/SettingsHub';
import StorefrontSettings from './components/shops/settings/StorefrontSettings';
import RewardsSettings from './components/shops/settings/RewardsSettings';
import CounterSettings from './components/shops/settings/CounterSettings';
import AccountSettings from './components/shops/settings/AccountSettings';
import TagOrderSettings from './components/shops/settings/TagOrderSettings';
import TillKit from './components/shops/TillKit';
import MemberCards from './components/shops/MemberCards';
import AssistedSignup from './components/shops/AssistedSignup';
import OwnerMorePanel from './components/shops/OwnerMorePanel';
import ForShops from './components/ForShops';
import { CompRedemptionLanding } from './pages/CompRedemptionLanding';
import NfcTap from './pages/NfcTap';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { Terms } from './pages/Terms';
import { MerchantTerms } from './pages/MerchantTerms';
import NotFound from './components/NotFound';
import { useAuthStore } from './store/authStore';
import { InstallPrompt } from './components/InstallPrompt';
import { StampClaimBanner } from './components/StampClaimBanner';
import { SWUpdateToast } from './components/SWUpdateToast';
import { ConsentGate } from './components/consent/ConsentGate';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';

// Layout for pages that still need the old global header (shops mostly).
// Header sits in the same max-width container as `main` so its retro-border
// box aligns with the page's panels instead of running full-bleed (mobile
// is unaffected — `container` has no max-width below the sm breakpoint).
function MainLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh flex flex-col bg-background safe-shell">
      <div className="container mx-auto px-4">
        <Header />
      </div>
      <main className="flex-1 container mx-auto px-4 py-8">
        {children}
      </main>
      <Footer />
    </div>
  );
}

// Layout for the game dashboard (header is built into Dashboard itself; the
// legal links row below is the only footer content this layout needs).
// Coral, not pixel — GameLayout wraps only the Dashboard route, which is
// fully coral-restyled (Task 16); the old CRT-scanline/neon-footer chrome
// used to run underneath every coral tab, which no longer matches.
function GameLayout({ children }: { children: React.ReactNode }) {
  // True app frame per the mockup: no padding, no footer — the shell owns the
  // full viewport, header edge-to-edge, tab bar at the bottom. Legal links
  // live on the MORE tab now. No safe-shell either: CoralHeader paints under
  // the status bar / Dynamic Island and pads its own content with
  // safe-area-inset-top; TabBar handles the bottom inset. Insetting here
  // would leave a cream strip under the clock.
  return (
    <div className="h-dvh bg-background relative">
      {children}
    </div>
  );
}

// Public routes accessible without authentication
// '/nfc' is public: a tag tap must land for a signed-out stranger — that is the
// onboarding funnel. NfcTap itself gates on the session and routes to sign-in.
const PUBLIC_ROUTES = ['/for-shops', '/auth/callback', '/redeem-comp', '/nfc', '/privacy', '/terms', '/merchant-terms'];

function App() {
  const { isAuthenticated, initialize } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    initialize();
    // Capture a referral link (?ref=<referrer address>) before OAuth strips the
    // query string, so it survives the round-trip and can be attached at signup.
    try {
      const ref = new URLSearchParams(window.location.search).get('ref');
      if (ref && ref.startsWith('0x')) localStorage.setItem('bitbrew_referral_ref', ref);
    } catch { /* ignore */ }
  }, [initialize]);

  const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);

  if (!isAuthenticated && !isPublicRoute) {
    return <OAuthLogin />;
  }

  const routes = (
    <Routes>
      <Route path="/" element={<GameLayout><Dashboard /></GameLayout>} />
      <Route path="/for-shops" element={<ForShops />} />
      <Route path="/shops" element={<Navigate to="/" replace />} />
      <Route path="/shops/:id" element={<MainLayout><ShopDetail /></MainLayout>} />
      <Route path="/shops/owner/dashboard" element={<ShopOwnerDashboard />} />
      <Route path="/shops/owner/more" element={<OwnerMorePanel />} />
      <Route path="/shop/analytics" element={<ShopAnalyticsDashboard />} />
      <Route path="/shops/register" element={<ShopRegistration />} />
      {/* OwnerShell (rendered inside each of these) is the layout now — no
          MainLayout wrapper, same normalization as the dashboard/analytics
          routes above. */}
      <Route path="/shops/settings" element={<SettingsHub />} />
      <Route path="/shops/settings/storefront" element={<StorefrontSettings />} />
      <Route path="/shops/settings/rewards" element={<RewardsSettings />} />
      <Route path="/shops/settings/counter" element={<CounterSettings />} />
      <Route path="/shops/settings/account" element={<AccountSettings />} />
      <Route path="/shops/settings/tag" element={<TagOrderSettings />} />
      <Route path="/shops/till-kit" element={<TillKit />} />
      <Route path="/shops/member-cards" element={<MemberCards />} />
      <Route path="/shops/assist" element={<AssistedSignup />} />
      <Route path="/auth/callback" element={<OAuthLogin />} />
      <Route path="/redeem-comp" element={<CompRedemptionLanding />} />
      <Route path="/nfc" element={<GameLayout><NfcTap /></GameLayout>} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/merchant-terms" element={<MerchantTerms />} />
      <Route path="/hq" element={<Navigate to="/" replace />} />
      {/* Catch-all. Without it an unknown path rendered a blank black page. */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );

  // Consent gate only applies to authenticated, non-public routes — the
  // public routes (incl. /privacy, /terms, /auth/callback mid-OAuth) must
  // stay reachable without first clearing consent, otherwise a user can't
  // even read the documents the gate asks them to accept.
  return (
    <>
      {isAuthenticated && !isPublicRoute ? <ConsentGate>{routes}</ConsentGate> : routes}
      <InstallPrompt />
      <StampClaimBanner />
      <ExistingCardNotice />
      <SWUpdateToast />
      <Analytics />
      <SpeedInsights />
    </>
  );
}

export default App;
